import { resetBoardScrollMargin } from "@/components/utils/boardRect";
import { offScreenCoord } from "@/math/coordinate-utils";
import { distance2 } from "@/math/coordinates";
import { longClickDist } from "@/model/Settings";
import type { ViewportCoordinate } from "@/model/coordinates";
import { initViewportCoord } from "@/model/coordinates";

import { clientCoord } from "./coordinates";
import { eventTarget, isLeftClick, releaseCapture } from "./dom";

interface Pointer {
  id: number;
  draggable: boolean;
  pos: ViewportCoordinate;
  downPos: ViewportCoordinate;
}

const mouse = { downs: 0, ups: 0 };
const pointers: Record<number, Pointer> = {};

export let mousePointer = initViewportCoord();

export function mouseState() {
  const downs = mouse.downs;
  return { isButtonDownOnce: () => mouse.downs > mouse.ups && mouse.downs === downs + 1 };
}

export function pointer(id: number): Pointer | undefined {
  return pointers[id];
}

export function otherPointer(id: number): Pointer | undefined {
  for (const key in pointers) {
    if (!pointers[key].draggable && +key !== id) {
      return pointers[key];
    }
  }
}

export function nonMovedPointer(e: PointerEvent): Pointer | undefined {
  const p = pointer(e.pointerId);
  if (p && distance2(p.pos, p.downPos) < longClickDist) {
    return p;
  }
}

export function pointerDown(id: number, pos: ViewportCoordinate, draggable: boolean) {
  pointers[id] = { id, draggable, pos, downPos: { ...pos } };
}

interface Handlers {
  down?: (id: number, pos: ViewportCoordinate) => void;
  move?: (
    id: number,
    pos: ViewportCoordinate,
    moveFn: (id: number, pos: ViewportCoordinate) => void,
  ) => void;
  up?: (id: number, pos?: ViewportCoordinate, target?: HTMLElement) => void;
}

export function initPointerHandler(handlers: Handlers = {}) {
  document.addEventListener("pointerdown", (e: PointerEvent) => {
    releaseCapture(e);
    if (isLeftClick(e)) {
      down(e.pointerId, clientCoord(e));
    }
  });

  document.addEventListener("pointermove", (e: PointerEvent) => {
    const pos = clientCoord(e);
    if (e.pointerType === "mouse") {
      mousePointer = pos;
    }
    move(e.pointerId, pos);
  });

  document.addEventListener("pointerup", (e: PointerEvent) => {
    if (e.pointerType === "mouse") {
      mouse.ups = mouse.downs;
    }
    up(e.pointerId, eventTarget(e));
  });

  document.addEventListener("pointerleave", (e: PointerEvent) => {
    if (e.pointerType === "mouse") {
      mousePointer = offScreenCoord();
    }
    if (isLeftClick(e)) {
      up(e.pointerId);
    }
  });

  document.addEventListener("pointerenter", (e: PointerEvent) => {
    if (!isLeftClick(e)) {
      if (e.pointerType === "mouse") {
        mouse.ups = mouse.downs;
      }
    }
  });

  function down(id: number, pos: ViewportCoordinate) {
    mouse.downs++;
    pointerDown(id, pos, false);
    handlers.down?.(id, pos);
  }

  function move(id: number, pos: ViewportCoordinate) {
    if (!pointers[id]) {
      return;
    }
    pointers[id].pos = pos;
    handlers.move?.(id, pos, move);
  }

  function up(id: number, target?: HTMLElement) {
    handlers.up?.(id, pointers[id]?.pos, target);
    delete pointers[id];
    resetBoardScrollMargin();
  }
}
